import { useState } from "react";
import { ArrowRight } from "lucide-react";
import PageShell from "../components/PageShell";
import TextField from "../components/TextField";
import FormStatus from "../components/FormStatus";
import useSeo from "../hooks/useSeo";
import { api } from "../lib/api";
import { images } from "../data/content";

const initialState = {
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  roles_needed: "",
  hiring_timeline: "",
  budget_range: "",
  message: "",
};

export default function EmployerForm() {
  useSeo("Employer Lead Form", "Tell TalentBridge Consulting about your job posting or candidate sourcing needs in Calgary.");
  const [form, setForm] = useState(initialState);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const onChange = (event) => setForm({ ...form, [event.target.name]: event.target.value });
  const onSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      await api.employerLead(form);
      setForm(initialState);
      setStatus("success");
    } catch (error) {
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <section className="bg-white px-5 pb-16 pt-32 md:px-8 lg:pb-24" data-testid="employer-page-section">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.85fr_1.15fr]">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#0066FF]" data-testid="employer-eyebrow">For employers</p>
            <h1 className="mt-5 font-heading text-4xl font-black leading-tight text-[#0A2540] sm:text-5xl" data-testid="employer-title">Start a hiring conversation.</h1>
            <p className="mt-6 text-base leading-8 text-[#475569]" data-testid="employer-description">
              Share the role, timing, and hiring context. TalentBridge can help with job posting support and candidate sourcing.
            </p>
            <img src={images.boardroom} alt="Boardroom meeting for employer hiring consultation" className="mt-10 aspect-[4/3] w-full border border-[#E2E8F0] object-cover" data-testid="employer-image" />
          </div>
          <form onSubmit={onSubmit} className="grid gap-5 border border-[#E2E8F0] bg-[#FBFBFC] p-6 md:p-8" data-testid="employer-lead-form">
            <FormStatus status={status} successText="Employer lead saved to the CRM pipeline." />
            <div className="grid gap-5 md:grid-cols-2">
              <TextField id="company_name" label="Company name" value={form.company_name} onChange={onChange} />
              <TextField id="contact_name" label="Contact name" value={form.contact_name} onChange={onChange} />
              <TextField id="email" label="Work email" type="email" value={form.email} onChange={onChange} />
              <TextField id="phone" label="Phone" value={form.phone} onChange={onChange} />
              <TextField id="roles_needed" label="Roles needed" value={form.roles_needed} onChange={onChange} />
              <TextField id="hiring_timeline" label="Hiring timeline" value={form.hiring_timeline} onChange={onChange} />
            </div>
            <TextField id="budget_range" label="Budget or salary range" value={form.budget_range} onChange={onChange} />
            <TextField id="message" label="Hiring context" multiline value={form.message} onChange={onChange} />
            <button type="submit" disabled={loading} className="inline-flex items-center justify-center gap-2 bg-[#0A2540] px-6 py-4 font-bold text-white transition-transform hover:-translate-y-0.5 hover:bg-[#051424] disabled:opacity-60" data-testid="employer-submit-button">
              {loading ? "Saving..." : "Submit hiring request"} <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        </div>
      </section>
    </PageShell>
  );
}